import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileText } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState, KpiCard } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { inr, dmy, today } from "@/lib/format";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August",
  "September", "October", "November", "December"];
const METHODS = ["cash", "bank", "upi", "card", "cheque", "other"];

const EMPTY = { name: "", category: "other", amount: "", expense_date: today(),
  payment_method: "cash", module: "business", reference: "", description: "",
  gst_percent: "0", gstin: "" };

export default function Expenses() {
  const { can, user } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [formError, setFormError] = useState("");
  const [attachFor, setAttachFor] = useState(null);
  const fileRef = useRef(null);
  const canManage = can("pharmacy.expenses.manage") || can("hospital.expenses.manage");
  const modules = user?.modules || [];

  const load = useCallback(async () => {
    setData(null);
    try {
      const res = await api.get("/expenses", { params: { month, year } });
      setData(res.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [month, year]);

  useEffect(() => { if (canManage) load(); }, [load, canManage]);
  if (!canManage) return <NoAccess what="expenses" />;

  const submit = async (e) => {
    e.preventDefault(); setFormError("");
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount || 0),
        gst_percent: Number(form.gst_percent || 0) });
      toast.success("Expense recorded.");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { setFormError(apiError(err)); }
  };

  const upload = async (file) => {
    if (!file || !attachFor) return;
    const body = new FormData();
    body.append("file", file);
    body.append("label", "receipt");
    try {
      await api.post(`/expenses/${attachFor.expense_id}/attachment`, body,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attachment uploaded.");
      setAttachFor(null); load();
    } catch (err) { toast.error(apiError(err)); }
    if (fileRef.current) fileRef.current.value = "";
  };

  const openDoc = async (exp, doc) => {
    try {
      const res = await api.get(`/expenses/${exp.expense_id}/attachment/${doc.document_id}`,
        { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank", "noopener");
    } catch (err) { toast.error(apiError(err)); }
  };

  const download = () => {
    const rows = [["Date", "Name", "Category", "Module", "Method", "Reference", "Amount"]].concat(
      (data?.items || []).map((i) => [i.expense_date, i.name, i.category, i.module,
        i.payment_method, i.reference || "", i.amount]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `expenses-${year}-${String(month).padStart(2, "0")}.csv`;
    a.click();
  };

  return (
    <div data-testid="expenses-page">
      <PageHeader overline="Business" title="Expenses"
        subtitle="One expense ledger for the whole business — salaries, rent, utilities, purchases, referral payouts and more."
        actions={<button onClick={() => { setForm(EMPTY); setFormError(""); setOpen(true); }}
          className={primaryBtnCls} data-testid="add-expense-btn">Add expense</button>} />

      <div className="mb-6 flex flex-wrap items-end gap-3">
        <select className={`${inputCls} sm:!w-44`} value={month} data-testid="expense-month-select"
          onChange={(e) => setMonth(Number(e.target.value))}>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <input type="number" className={`${inputCls} sm:!w-28`} value={year} data-testid="expense-year-input"
          onChange={(e) => setYear(Number(e.target.value))} />
        <button onClick={() => window.print()} className={ghostBtnCls} data-testid="expense-print-btn">Print</button>
        <button onClick={download} className={ghostBtnCls} data-testid="expense-csv-btn">CSV</button>
      </div>

      {!data ? <Loading /> : (
        <>
          <div className="mb-8 grid gap-4 sm:grid-cols-3">
            <KpiCard label="Entries" value={data.totals.count} testid="kpi-expense-count" />
            <KpiCard label="Total expenses" value={inr(data.totals.amount)} tone="accent" testid="kpi-expense-total" />
            <KpiCard label="Categories used" value={Object.keys(data.by_category).length} testid="kpi-expense-categories" />
          </div>

          {Object.keys(data.by_category).length > 0 && (
            <div className="panel mb-8 p-5" data-testid="expense-category-summary">
              <p className="overline mb-4 text-[#EA580C]">By category</p>
              <ul className="grid gap-2 text-sm sm:grid-cols-3">
                {Object.entries(data.by_category).map(([k, v]) => (
                  <li key={k} className="flex justify-between border-b border-slate-100 pb-1.5">
                    <span className="capitalize">{k.replace(/_/g, " ")}</span><b>{inr(v)}</b>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Table testid="expenses-table"
            columns={["Date", "Expense", "Category", "Module", "Method", "Reference", "GST", "Amount", ""]}
            rows={data.items}
            empty={<EmptyState title="No expenses this month" subtitle="Record rent, utilities, salaries or other costs." testid="expenses-empty" />}
            renderRow={(e) => (
              <tr key={e.expense_id} data-testid={`expense-row-${e.expense_id}`}>
                <Td className="whitespace-nowrap text-slate-500">{dmy(e.expense_date)}</Td>
                <Td className="font-medium text-slate-900">{e.name}
                  <span className="block text-xs text-slate-400">{e.description || "—"}</span></Td>
                <Td className="capitalize text-slate-600">{(e.category || "").replace(/_/g, " ")}</Td>
                <Td className="capitalize text-slate-600">{e.module}</Td>
                <Td className="capitalize text-slate-600">{e.payment_method}</Td>
                <Td className="text-slate-600">{e.reference || "—"}</Td>
                <Td className="text-slate-500">{e.gst_amount ? inr(e.gst_amount) : "—"}</Td>
                <Td className="font-semibold text-slate-900">{inr(e.amount)}</Td>
                <Td>
                  <div className="flex flex-wrap justify-end gap-2">
                    {(e.documents || []).map((d) => (
                      <button key={d.document_id} onClick={() => openDoc(e, d)} className={ghostBtnCls}
                        data-testid={`expense-doc-${d.document_id}`}>
                        <FileText className="h-3.5 w-3.5" />
                      </button>
                    ))}
                    <button onClick={() => { setAttachFor(e); setTimeout(() => fileRef.current?.click(), 50); }}
                      className={ghostBtnCls} data-testid={`attach-expense-${e.expense_id}`}>
                      <Upload className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </Td>
              </tr>
            )} />
        </>
      )}

      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
        data-testid="expense-file-input" onChange={(e) => upload(e.target.files?.[0])} />

      <Modal open={open} onClose={() => setOpen(false)} wide title="Add expense"
        subtitle="Attach a receipt after saving." testid="expense-modal">
        <form onSubmit={submit} className="grid gap-5 sm:grid-cols-3" data-testid="expense-form">
          <Field label="Expense name *" className="sm:col-span-2">
            <input required minLength={2} className={inputCls} value={form.name} data-testid="expense-name-input"
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Category *">
            <select className={inputCls} value={form.category} data-testid="expense-category-select"
              onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {(data?.categories || ["other"]).map((c) => (
                <option key={c} value={c}>{c.replace(/_/g, " ")}</option>
              ))}
            </select>
          </Field>
          <Field label="Amount (₹) *">
            <input required type="number" min="0.01" step="0.01" className={inputCls} value={form.amount}
              data-testid="expense-amount-input" onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </Field>
          <Field label="Date *">
            <input required type="date" className={inputCls} value={form.expense_date}
              data-testid="expense-date-input" onChange={(e) => setForm({ ...form, expense_date: e.target.value })} />
          </Field>
          <Field label="Payment method">
            <select className={inputCls} value={form.payment_method} data-testid="expense-method-select"
              onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
              {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Applies to">
            <select className={inputCls} value={form.module} data-testid="expense-module-select"
              onChange={(e) => setForm({ ...form, module: e.target.value })}>
              <option value="business">Whole business</option>
              {modules.includes("hospital") && <option value="hospital">Hospital</option>}
              {modules.includes("pharmacy") && <option value="pharmacy">Pharmacy</option>}
            </select>
          </Field>
          <Field label="Reference / bill no.">
            <input className={inputCls} value={form.reference} data-testid="expense-reference-input"
              onChange={(e) => setForm({ ...form, reference: e.target.value })} />
          </Field>
          <Field label="GST % (if claimable)">
            <input type="number" min="0" className={inputCls} value={form.gst_percent}
              data-testid="expense-gst-input" onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} />
          </Field>
          <Field label="Vendor GSTIN">
            <input className={inputCls} value={form.gstin} data-testid="expense-gstin-input"
              onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} />
          </Field>
          <Field label="Description" className="sm:col-span-2">
            <input className={inputCls} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          {formError && <p className="text-sm text-red-600 sm:col-span-3" data-testid="expense-form-error">{formError}</p>}
          <div className="sm:col-span-3">
            <button className={primaryBtnCls} data-testid="expense-submit-btn">Save expense</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
